//@flow
import * as React from 'react';
import classnames from 'classnames';
import type { List } from 'immutable';
import { themr } from 'react-css-themr';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import { ButtonWithPopover } from 'common/components/core/button';
import theme from './TableButtonWithPopover.scss';

type Props = {
    list: List<Object>,
    theme: { [key: string]: string },
    listTitle: string,
    listEditTitle?: string,
    listLeftIcon: React.Element<any> | any,
    className?: string,
    buttonTooltip?: string,
    ripple: boolean,
    preferPlace: string,
    popOverClassName?: string,
    children?: React.Element<any>,
    label?: string,
    buttonType: string,
    isListSaveInProgress?: boolean,
    defaultIsListEditable?: boolean,
    editedItems?: Array<Object>,
    onListEnterKeyPress?: () => void,
    onListChange?: () => void,
    onListSave?: () => void,
    onListEditClick?: () => void,
    onListEditCancel?: () => void,
    defaultAddButton?: boolean,
    showHyphenForEmpty?: boolean
};

type State = {
    isButtonPopoverOpen: boolean,
    isListModified?: boolean,
    isListEditable?: boolean
};

type FactoryParams = {
    ListElement: React.Component<*, *>
};

const tableButtonWithPopoverFactory = ({ ListElement }: FactoryParams) => {
    class TableButtonWithPopover extends React.PureComponent<Props, State> {
        state: State;
        static defaultProps = {
            ripple: false,
            showHyphenForEmpty: true,
            preferPlace: PopoverPlaceEnum.Row
        };
        constructor(props: Props) {
            super(props);
            this.state = {
                isButtonPopoverOpen: false,
                notClosable: false,
                isListEditable: props.defaultIsListEditable
            };
        }
        togglePopover = () => {
            if (this.state.isButtonPopoverOpen) {
                this.hidePopover();
            } else {
                this.showPopover();
            }
        };
        hidePopover = () => {
            this.onListEditCancel();
            this.setState({ isButtonPopoverOpen: false });
        };
        showPopover = () => {
            const { defaultIsListEditable } = this.props;
            this.setState({
                isButtonPopoverOpen: true,
                isListEditable: defaultIsListEditable
            });
        };
        onOuterAction = () => {
            const { isListModified } = this.state;
            if (!isListModified) {
                this.hidePopover();
            }
        };
        onEscape = () => {
            const { isListModified } = this.state;
            if (!isListModified) {
                this.hidePopover();
            }
        };
        onListChange = editedItems => {
            const { onListChange } = this.props;
            if (editedItems.length > 0) {
                this.setState({ isListModified: true });
            } else {
                this.setState({ isListModified: false });
            }
            onListChange && onListChange(editedItems);
        };
        onListEditClick = () => {
            const { onListEditClick } = this.props;
            this.toggleListEditable();
            onListEditClick && onListEditClick();
        };
        toggleListEditable = () => {
            const { isListEditable } = this.state;
            if (isListEditable) {
                this.onListEditCancel();
            } else {
                this.setState({ isListEditable: true });
            }
        };
        onListEditCancel = () => {
            const { onListEditCancel } = this.props;
            this.setState({ isListEditable: false, isListModified: false });
            onListEditCancel && onListEditCancel();
        };
        onListSave = () => {
            const { onListSave } = this.props;
            this.setState({ isListEditable: false, isListModified: false });
            onListSave && onListSave();
        };
        handleListEnterKeyPress = () => {
            const { isListModified } = this.state;
            const { onListEnterKeyPress } = this.props;
            if (isListModified) {
                this.onListSave();
            }
            onListEnterKeyPress && onListEnterKeyPress();
        };
        render() {
            const {
                list,
                ripple,
                buttonTooltip,
                theme,
                listTitle,
                listEditTitle,
                listLeftIcon,
                className,
                popOverClassName,
                preferPlace,
                label,
                children,
                buttonType,
                isListSaveInProgress,
                editedItems,
                place,
                semibold,
                icon,
                showHyphenForEmpty,
                defaultAddButton,
                ...others
            } = this.props;
            const {
                isButtonPopoverOpen,
                isListModified,
                isListEditable
            } = this.state;
            const listSize = list && list.size;
            if (listSize > 0 || defaultAddButton) {
                return (
                    <DivWithPopover
                        className={theme.tableButtonWrap}
                        place={PopoverPlaceEnum.Row}
                        preferPlace={PopoverPlaceEnum.Right}
                        popOverContent={
                            isButtonPopoverOpen ? null : buttonTooltip
                        }
                        hidePopoverOnClick
                        triggerPopoverOn={PopoverTriggerEnum.Hover}
                    >
                        <ButtonWithPopover
                            buttonType={buttonType}
                            theme={theme}
                            isPopOverOpen={isButtonPopoverOpen}
                            onClick={this.togglePopover}
                            onOuterAction={this.onOuterAction}
                            onEscape={this.onEscape}
                            className={classnames(
                                theme.tableButton,
                                className,
                                {
                                    [theme.buttonPopoverOpen]: isButtonPopoverOpen
                                }
                            )}
                            ripple={ripple}
                            popOverClassName={classnames(
                                theme.listPopoverWrap,
                                popOverClassName
                            )}
                            semibold={semibold}
                            icon={icon}
                            popOverContent={
                                <ListElement
                                    {...others}
                                    onListEnterKeyPress={
                                        this.handleListEnterKeyPress
                                    }
                                    editedItems={editedItems}
                                    isListSaveInProgress={isListSaveInProgress}
                                    list={list}
                                    title={listTitle}
                                    editTitle={listEditTitle}
                                    leftIcon={listLeftIcon}
                                    isListEditable={isListEditable}
                                    isListModified={isListModified}
                                    onListSave={this.onListSave}
                                    onListClose={this.hidePopover}
                                    onListChange={this.onListChange}
                                    onListEditClick={this.onListEditClick}
                                    onListEditCancel={this.onListEditCancel}
                                    theme={theme}
                                />
                            }
                            invertPopover={false}
                            place={place}
                            preferPlace={preferPlace}
                            label={
                                label ? (
                                    <span className={theme.buttonLabel}>
                                        {label}
                                    </span>
                                ) : null
                            }
                        >
                            {children ? (
                                <span className={theme.buttonLabel}>
                                    {children}
                                </span>
                            ) : null}
                        </ButtonWithPopover>
                    </DivWithPopover>
                );
            }
            return showHyphenForEmpty ? (
                <div className={theme.emptyButton}>–</div>
            ) : null;
        }
    }
    return themr('TableButtonWithPopover', theme)(TableButtonWithPopover);
};

export default tableButtonWithPopoverFactory;
export { tableButtonWithPopoverFactory };
